import { useState, useEffect } from "react";
import api from "../api/client";

export default function BadgesView({ studentId, isCompact = false }) {
  const [badgeData, setBadgeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);
  const [newBadge, setNewBadge] = useState(null);

  useEffect(() => {
    fetchBadges();
    checkNewBadges();
  }, [studentId]);

  const fetchBadges = async () => {
    try {
      const res = await api.get(`/badges/${studentId}`);
      setBadgeData(res.data);
    } catch (e) {
      console.error("Failed to fetch badges", e);
    } finally {
      setLoading(false);
    }
  };

  const checkNewBadges = async () => {
    try {
      const res = await api.post(`/badges/check/${studentId}`);
      if (res.data.new_badges?.length > 0) {
        setNewBadge(res.data.new_badges[0]);
        setShowCelebration(true);
        // Refresh badges list
        fetchBadges();
        // Hide celebration after 5 seconds
        setTimeout(() => setShowCelebration(false), 5000);
      }
    } catch (e) {
      // Silent fail - badge check is optional
    }
  };

  if (loading) {
    return (
      <div className="animate-pulse space-y-4">
        <div className="h-8 bg-gray-200 rounded w-1/3"></div>
        <div className="grid grid-cols-4 gap-4">
          {[1,2,3,4].map(i => (
            <div key={i} className="h-24 bg-gray-200 rounded-xl"></div>
          ))}
        </div>
      </div>
    );
  }

  // Compact view for dashboard sidebar
  if (isCompact) {
    return (
      <div className="glass-card rounded-xl p-4">
        <div className="flex items-center justify-between mb-3">
          <h4 className="font-semibold text-gray-800 text-sm" style={{ fontFamily: "Outfit, sans-serif" }}>
            Mes Badges
          </h4>
          <span className="text-xs font-bold text-[#1a2744] bg-[#1a2744]/10 px-2 py-1 rounded-full">
            {badgeData?.total_points || 0} pts
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          {badgeData?.badges?.filter(b => b.earned).slice(0, 6).map(badge => (
            <div 
              key={badge.key} 
              className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-100 to-amber-200 flex items-center justify-center text-xl"
              title={`${badge.name} - ${badge.description}`}
            >
              {badge.icon}
            </div>
          ))}
          {badgeData?.earned_count === 0 && (
            <p className="text-xs text-gray-400">Aucun badge encore</p>
          )}
        </div>
        <p className="text-xs text-gray-500 mt-2">
          {badgeData?.earned_count}/{badgeData?.available_count} badges débloqués
        </p>
      </div>
    );
  }

  // Full view
  const categories = [
    { key: "navigation", name: "Navigation", textColor: "text-cyan-700", dotColor: "bg-cyan-500" },
    { key: "immo", name: "Immobilier", textColor: "text-emerald-700", dotColor: "bg-emerald-500" },
    { key: "engagement", name: "Engagement", textColor: "text-violet-700", dotColor: "bg-violet-500" },
    { key: "session", name: "Sessions", textColor: "text-amber-700", dotColor: "bg-amber-500" },
  ];

  return (
    <div className="space-y-6">
      {/* Celebration Modal */}
      {showCelebration && newBadge && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-white rounded-2xl p-8 max-w-sm mx-4 text-center transform animate-bounce-in">
            <div className="text-6xl mb-4">{newBadge.icon}</div>
            <h3 className="text-2xl font-bold text-gray-900 mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
              Nouveau Badge !
            </h3>
            <p className="text-lg font-semibold text-[#1a2744]">{newBadge.name}</p>
            <p className="text-sm text-gray-500 mt-2">{newBadge.description}</p>
            <div className="mt-4 inline-flex items-center gap-1 bg-amber-100 text-amber-700 px-3 py-1 rounded-full text-sm font-bold">
              +{newBadge.points} points
            </div>
            <button 
              onClick={() => setShowCelebration(false)}
              className="btn-primary w-full mt-6"
            >
              Super ! 🎉
            </button>
          </div>
        </div>
      )}

      {/* Header with total points */}
      <div className="glass-card rounded-2xl p-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900" style={{ fontFamily: "Outfit, sans-serif" }}>
              Mes Badges ⚓
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {badgeData?.earned_count || 0}/{badgeData?.available_count || 0} badges débloqués
            </p>
          </div>
          <div className="text-right">
            <span className="text-3xl font-bold text-[#1a2744]">{badgeData?.total_points || 0}</span>
            <p className="text-xs text-gray-500">points totaux</p>
          </div>
        </div>
        
        {/* Progress bar */}
        <div className="mt-4">
          <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-[#1a2744] to-[#d4a012] rounded-full transition-all duration-500"
              style={{ width: `${(badgeData?.earned_count / badgeData?.available_count * 100) || 0}%` }}
            />
          </div>
        </div>
      </div>

      {/* Badges by category */}
      {categories.map(cat => {
        const catBadges = badgeData?.badges?.filter(b => b.category === cat.key) || [];
        if (catBadges.length === 0) return null;
        
        return (
          <div key={cat.key} className="glass-card rounded-2xl p-5">
            <h3 className={`font-semibold ${cat.textColor} mb-4 flex items-center gap-2`} style={{ fontFamily: "Outfit, sans-serif" }}>
              <span className={`w-2 h-2 rounded-full ${cat.dotColor}`}></span>
              {cat.name}
            </h3>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
              {catBadges.map(badge => (
                <div 
                  key={badge.key}
                  className={`relative rounded-xl p-4 text-center transition-all ${
                    badge.earned 
                      ? "bg-gradient-to-br from-amber-50 to-amber-100 border-2 border-amber-200 shadow-sm" 
                      : "bg-gray-50 border-2 border-dashed border-gray-200 opacity-50"
                  }`}
                >
                  {badge.earned && (
                    <div className="absolute -top-2 -right-2 w-6 h-6 bg-emerald-500 rounded-full flex items-center justify-center shadow-sm">
                      <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  )}
                  <div className={`text-3xl mb-2 ${!badge.earned && "grayscale"}`}>{badge.icon}</div>
                  <p className="font-semibold text-sm text-gray-800">{badge.name}</p>
                  <p className="text-xs text-gray-500 mt-1 line-clamp-2">{badge.description}</p>
                  <div className={`mt-2 text-xs font-bold ${badge.earned ? "text-amber-600" : "text-gray-400"}`}>
                    {badge.points} pts
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      
      {/* Fallback if no badges loaded */}
      {(!badgeData?.badges || badgeData.badges.length === 0) && !loading && (
        <div className="glass-card rounded-2xl p-8 text-center">
          <div className="text-4xl mb-4">🎖️</div>
          <p className="text-gray-500">Chargement des badges en cours...</p>
          <button 
            onClick={() => { setLoading(true); fetchBadges(); }}
            className="btn-secondary mt-4"
          >
            Recharger
          </button>
        </div>
      )}
    </div>
  );
}
